/* ============================================================================
 * serverOperationBuilder.ts - Server operation construction utilities
 * ============================================================================ */

import type { ProjectData, NodeOperation } from '@/shared/types/projectData';
import { getNodeId, getProjectIdFromNodeId } from '@/features/projects/MultiProjectContext';
import { log } from '@/shared/utils/logger';
import { normalizeKanbanColumnId, normalizeKanbanStatus } from '@/shared/utils/kanbanState';

interface ServerOperationResult {
  serverOperation: Record<string, unknown>;
  projectId: string | null;
}

function normalizeNodeDataIds(data: Record<string, unknown>): Record<string, unknown> {
  if (!data || typeof data !== 'object') {
    return data;
  }

  const normalized = { ...data };

  if (normalized.id) {
    normalized.id = getNodeId(normalized.id as string);
  }
  if (normalized.parentId) {
    normalized.parentId = getNodeId(normalized.parentId as string);
  }
  if (normalized.parent_id) {
    normalized.parent_id = getNodeId(normalized.parent_id as string);
  }
  if (Array.isArray(normalized.dependencies)) {
    normalized.dependencies = normalized.dependencies.map((dep: string) => getNodeId(dep));
  }
  if (normalized.status) {
    normalized.status = normalizeKanbanStatus(normalized.status as string);
  }
  if ((normalized.viewState as Record<string, unknown>)?.kanban) {
    const vs = normalized.viewState as Record<string, Record<string, unknown>>;
    normalized.viewState = {
      ...vs,
      kanban: {
        ...vs.kanban,
        columnId: normalizeKanbanColumnId(vs.kanban.columnId as string),
      },
    };
  }

  return normalized;
}

/**
 * Convert client operation to server operation format
 * Handles namespaced ID conversion and project ID extraction
 */
export function buildServerOperation(
  operation: NodeOperation,
  projectData: ProjectData,
  sessionId: string,
  operationId?: string
): ServerOperationResult {
  const serverOperation: Record<string, unknown> = { ...operation, sessionId };
  if (operationId) {
    serverOperation.id = operationId;
  }
  let projectId: string | null =
    typeof operation.metadata?.projectId === 'string' ? operation.metadata.projectId as string : null;

  // Extract project ID and clean up namespaced IDs based on operation type
  switch (operation.type) {
    case 'update':
      if (operation.nodeId) {
        projectId = getProjectIdFromNodeId(operation.nodeId);
        serverOperation.nodeId = getNodeId(operation.nodeId);
      }
      // CRITICAL FIX: Include the data field for update operations
      if (operation.data) {
        serverOperation.data = normalizeNodeDataIds(operation.data as unknown as Record<string, unknown>);
      }
      break;

    case 'delete':
    case 'delete-tree':
    case 'restore-tree':
    case 'move':
      if (operation.nodeId) {
        projectId = getProjectIdFromNodeId(operation.nodeId);
        serverOperation.nodeId = getNodeId(operation.nodeId);
      }
      if (operation.type === 'move') {
        if (Array.isArray(operation.nodeIds)) {
          serverOperation.nodeIds = operation.nodeIds.map(id => getNodeId(id));
        }
        if (typeof operation.sourceParentId === 'string') {
          serverOperation.sourceParentId = getNodeId(operation.sourceParentId);
        } else if (operation.sourceParentId === null) {
          serverOperation.sourceParentId = null;
        }
        if (Array.isArray(operation.sourceNodeIds)) {
          serverOperation.sourceNodeIds = operation.sourceNodeIds.map(id => getNodeId(id));
        }
      }
      break;

    case 'paste-nodes': {
      projectId = operation.metadata?.projectId as string || getProjectIdFromNodeId(operation.pasteParentId || '');
      serverOperation.pasteParentId = getNodeId(operation.pasteParentId || '');
      serverOperation.clipboardData = operation.clipboardData?.map(node => normalizeNodeDataIds(node as unknown as Record<string, unknown>));
      serverOperation.cutNodeIds = operation.cutNodeIds?.map(getNodeId);
      serverOperation.sourceNodeIds = operation.sourceNodeIds?.map(getNodeId);
      serverOperation.duplicatedNodeMappings = operation.duplicatedNodeMappings?.map(mapping => ({originalId:getNodeId(mapping.originalId),newId:getNodeId(mapping.newId)}));
      break;
    }

    case 'progress-update':
      // FIX: Convert progress-update to regular update since backend doesn't recognize progress-update
      if (operation.nodeId) {
        projectId = getProjectIdFromNodeId(operation.nodeId);
        serverOperation.nodeId = getNodeId(operation.nodeId);
        // Change type to 'update' and put progress in data field
        serverOperation.type = 'update';
        serverOperation.data = { progress: operation.progress };
        // Remove the standalone progress field as it's now in data
        delete serverOperation.progress;
      }
      break;

    case 'batch-update':
      if (operation.updates && operation.updates.length > 0) {
        // Check first update for project ID
        const firstUpdate = operation.updates[0];
        if (firstUpdate.nodeId) {
          projectId = getProjectIdFromNodeId(firstUpdate.nodeId);
          // Clean all node IDs in updates
          serverOperation.updates = operation.updates.map(update => ({
            ...update,
            nodeId: getNodeId(update.nodeId),
            data: normalizeNodeDataIds(update.data as unknown as Record<string, unknown>)
          }));
        }
      }
      break;

    case 'batch-delete':
      if (operation.nodeIds && operation.nodeIds.length > 0) {
        projectId = getProjectIdFromNodeId(operation.nodeIds[0]);
        serverOperation.nodeIds = operation.nodeIds.map(id => getNodeId(id));
      }
      break;

    case 'create': {
      if (operation.parentId) {
        projectId = getProjectIdFromNodeId(operation.parentId);
        if (projectId?.includes(',')) {
          projectId = projectId.split(',')[0].trim();
        }
        serverOperation.parentId = getNodeId(operation.parentId) === 'ROOT' ? undefined : getNodeId(operation.parentId);
      } else {
        projectId = (operation.data as Partial<Record<string, unknown>> | undefined)?.projectId as string || projectData?.projectId;
      }

      // Set up data with node ID
      const cleanNodeId = operation.nodeId
        ? getNodeId(operation.nodeId)
        : (operation.data?.id ? getNodeId(operation.data.id) : undefined);
      if (operation.data) {
        serverOperation.data = normalizeNodeDataIds(operation.data as unknown as Record<string, unknown>);
        (serverOperation.data as Record<string, unknown>).id = cleanNodeId;
        delete (serverOperation.data as Record<string, unknown>).projectId;
      } else {
        serverOperation.data = { id: cleanNodeId };
      }

      // Calculate order: use targetIndex if specified, otherwise append to end
      if (operation.targetIndex !== undefined) {
        serverOperation.targetIndex = operation.targetIndex;
      } else {
        // Append to end of siblings
        const siblings = projectData.nodes.filter(n => n.parentId === operation.parentId);
        serverOperation.targetIndex = siblings.length;
      }
      break;
    }

    case 'group-move':
      // Frontend now sends nodeId for group-move operations
      if (operation.nodeId) {
        const nodeProjectId = getProjectIdFromNodeId(operation.nodeId);
        if (!projectId || projectId === 'default') {
          projectId = nodeProjectId;
        }
        serverOperation.nodeId = getNodeId(operation.nodeId);
      }
      // CRITICAL: Include daysDelta for date-based group moves
      if (operation.daysDelta !== undefined) {
        serverOperation.daysDelta = operation.daysDelta;
      }
      log.debug('serverOperationBuilder', 'Sending group-move operation:', {
        operationId,
        sessionId,
        nodeId: serverOperation.nodeId,
        daysDelta: serverOperation.daysDelta
      });
      break;
  }

  // Clean up targetParentId when explicitly provided, including root/null moves.
  if (Object.prototype.hasOwnProperty.call(operation, 'targetParentId')) {
    serverOperation.targetParentId =
      operation.targetParentId === null
        ? null
        : getNodeId(operation.targetParentId);
  }

  return { serverOperation, projectId };
}

/**
 * Ensure project ID is set on server operation
 * Handles validation and fallback logic
 */
export function ensureProjectId(
  serverOperation: Record<string, unknown>,
  extractedProjectId: string | null,
  projectData: ProjectData,
  operation: NodeOperation
): void {
  const metadataProjectId = typeof operation.metadata?.projectId === 'string'
    ? operation.metadata.projectId as string
    : null;

  // Always include project ID in the message
  // CRITICAL FIX: Treat 'default' as invalid projectId (indicates non-namespaced ID)
  // This ensures we fall back to projectData.projectId for timeline drag operations
  if (metadataProjectId && metadataProjectId !== 'default') {
    serverOperation.projectId = metadataProjectId.includes(',')
      ? metadataProjectId.split(',')[0].trim()
      : metadataProjectId;
  } else if (extractedProjectId && extractedProjectId !== 'default') {
    // Final validation - ensure no comma-separated project IDs
    if (extractedProjectId.includes(',')) {
      log.warn('serverOperationBuilder', 'Project ID contains comma, using first ID:', extractedProjectId);
      extractedProjectId = extractedProjectId.split(',')[0].trim();
    }
    serverOperation.projectId = extractedProjectId;
  } else if (projectData?.projectId) {
    // Use the project ID from projectData if available
    let pid = projectData.projectId;
    // Validate it doesn't contain comma
    if (pid.includes(',')) {
      log.warn('serverOperationBuilder', 'ProjectData.projectId contains comma, using first ID:', pid);
      pid = pid.split(',')[0].trim();
    }
    serverOperation.projectId = pid;
  } else {
    // For multi-project mode, extract from the first affected node
    const firstNodeId = operation.nodeId || operation.nodeIds?.[0];
    if (firstNodeId) {
      let pid = getProjectIdFromNodeId(firstNodeId);
      // Validate extracted project ID
      if (pid.includes(',')) {
        log.warn('serverOperationBuilder', 'Extracted project ID contains comma, using first ID:', pid);
        pid = pid.split(',')[0].trim();
      }
      serverOperation.projectId = pid;
    }
  }
}
